import React, { useState, useEffect, useMemo } from 'react';

// ═══════════════════════════════════════════════════════════════════════════════
// HARDCODED FROM WORKBOOK — Book Chapter 7 equations
// ═══════════════════════════════════════════════════════════════════════════════

const COMPONENTS = [
  { key: 'lpiNorm',               label: 'LPI',               shortLabel: 'LPI',   tier: 'mid' },
  { key: 'waterNorm',             label: 'Water Access',       shortLabel: 'Water',  tier: 'mid' },
  { key: 'sanitationNorm',        label: 'Sanitation',         shortLabel: 'Sanit',  tier: 'top' },
  { key: 'electricityNorm',       label: 'Electricity',        shortLabel: 'Elec',   tier: 'mid' },
  { key: 'internetNorm',          label: 'Internet',           shortLabel: 'Intnt',  tier: 'top' },
  { key: 'educationNorm',         label: 'Education',          shortLabel: 'Educ',   tier: 'top' },
  { key: 'bankNorm',              label: 'Bank Access',        shortLabel: 'Bank',   tier: 'mid' },
  { key: 'capciNorm',             label: 'CAPCI',              shortLabel: 'CAPCI',  tier: 'top' },
  { key: 'gcrNorm',               label: 'GCR',                shortLabel: 'GCR',    tier: 'mid' },
  { key: 'ruleOfLawNorm',         label: 'Rule of Law',        shortLabel: 'RoL',    tier: 'low' },
  { key: 'realWageNorm',          label: 'Real Wage',          shortLabel: 'RWage',  tier: 'low' },
  { key: 'insuranceNorm',         label: 'Insurance',          shortLabel: 'Ins',    tier: 'low' },
  { key: 'corruptionControlNorm', label: 'Corruption Ctrl',    shortLabel: 'CorrC',  tier: 'low' },
  { key: 'physiciansNorm',        label: 'Physicians',         shortLabel: 'Phys',   tier: 'top' },
];

const W = [0.10, 0.09, 0.08, 0.09, 0.08, 0.08, 0.07, 0.07, 0.06, 0.06, 0.06, 0.05, 0.05, 0.06];

const TOTAL_EFFECTS = [
  0.6444, 0.6229, 0.6513, 0.6262, 0.7083, 0.6585, 0.6291,
  0.7294, 0.5992, 0.3871, 0.3086, 0.2251, 0.5679, 0.6495
];

function computeMGI(scores) {
  let s = 0, wSum = 0;
  for (let i = 0; i < 14; i++) {
    if (scores[i] !== null && scores[i] !== undefined) {
      s += W[i] * scores[i];
      wSum += W[i];
    }
  }
  return wSum > 0 ? s / wSum : 0;
}

function computeOpportunity(scores) {
  return scores.map((x, i) => TOTAL_EFFECTS[i] * (100 - (x || 0)));
}

function allocateWeightedCascade(scores, budget) {
  const alloc = new Array(14).fill(0);
  let remaining = budget;
  let active = Array.from({ length: 14 }, (_, i) => i);
  for (let iter = 0; iter < 20 && remaining > 0.01 && active.length; iter++) {
    const powered = active.map(i => Math.pow(TOTAL_EFFECTS[i], 3));
    const sumPow = powered.reduce((a, b) => a + b, 0);
    const newActive = [];
    let allocated = 0;
    active.forEach((ci, k) => {
      const share = remaining * (powered[k] / sumPow);
      const headroom = 100 - (scores[ci] || 0) - alloc[ci];
      const actual = Math.min(share, Math.max(0, headroom));
      alloc[ci] += actual;
      allocated += actual;
      if (actual >= share - 0.01) newActive.push(ci);
    });
    remaining -= allocated;
    active = newActive;
  }
  return alloc;
}

const tierColor = (tier) => tier === 'top' ? '#0f766e' : tier === 'mid' ? '#475569' : '#94a3b8';

// ═══════════════════════════════════════════════════════════════════════════════
// SIMULATION PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function SimulationPage() {
  const [allData, setAllData] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [originalScores, setOriginalScores] = useState(new Array(14).fill(0));
  const [scores, setScores] = useState(new Array(14).fill(0));
  const [budget, setBudget] = useState(20);
  const [countryName, setCountryName] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(`${process.env.PUBLIC_URL}/data/grademygovernment_data.json`).then(r => r.json()),
      fetch(`${process.env.PUBLIC_URL}/data/countries.json`).then(r => r.json()),
    ]).then(([data, ctrs]) => { setAllData(data); setCountries(ctrs); });
  }, []);

  const loadCountry = (code) => {
    if (!allData || !code) return;
    const country = allData['2023']?.find(c => c.code === code);
    if (!country) return;
    setSelectedCode(code);
    setCountryName(country.name);
    const s = COMPONENTS.map(c => country[c.key] ?? 0);
    setOriginalScores([...s]);
    setScores([...s]);
  };

  const originalMGI = useMemo(() => computeMGI(originalScores), [originalScores]);
  const newMGI = useMemo(() => computeMGI(scores), [scores]);
  const deltas = useMemo(() => scores.map((x, i) => x - originalScores[i]), [scores, originalScores]);
  const deltaDirect = useMemo(() => deltas.reduce((sum, dx, i) => sum + W[i] * dx, 0), [deltas]);
  const deltaCascade = useMemo(() => deltas.reduce((sum, dx, i) => sum + TOTAL_EFFECTS[i] * dx, 0), [deltas]);
  const opportunity = useMemo(() => computeOpportunity(originalScores), [originalScores]);

  const applyStrategy = (type) => {
    const s = [...originalScores];
    if (type === 'reset') { setScores([...originalScores]); return; }
    if (type === 'equal') {
      const per = budget / 14;
      setScores(s.map(x => Math.min(100, x + per)));
    } else if (type === 'highestWeight') {
      // All to LPI (highest weight=0.10)
      s[0] = Math.min(100, s[0] + budget);
      setScores(s);
    } else if (type === 'highestEffect') {
      // All to CAPCI (highest total effect)
      s[7] = Math.min(100, s[7] + budget);
      setScores(s);
    } else if (type === 'weighted') {
      const alloc = allocateWeightedCascade(originalScores, budget);
      setScores(s.map((x, i) => Math.min(100, x + alloc[i])));
    }
  };

  const setSlider = (i, val) => {
    const next = [...scores];
    next[i] = Math.max(0, Math.min(100, val));
    setScores(next);
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '2rem 1.5rem 4rem' }}>
      <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '2rem', color: '#0f172a', marginBottom: '0.3rem' }}>
        Governance Simulation
      </h1>
      <p style={{ color: '#64748b', marginBottom: '1.5rem', lineHeight: 1.7, fontSize: '0.95rem' }}>
        Explore how improving specific governance components affects a country's overall MGI score.
        Adjust sliders to model policy changes and see both the direct mechanical effect and the
        predicted long-term cascade effect through correlated components.
      </p>

      {/* Country selector + budget */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={labelSty}>Country (2023 data)</label>
          <select style={inputSty} value={selectedCode} onChange={e => loadCountry(e.target.value)}>
            <option value="">Select country...</option>
            {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
        <div style={{ width: 160 }}>
          <label style={labelSty}>Budget (points)</label>
          <input type="number" style={inputSty} value={budget} min={1} max={100}
            onChange={e => setBudget(Math.max(1, Math.min(100, Number(e.target.value))))} />
        </div>
      </div>

      {selectedCode && (
        <>
          {/* MGI Score Card */}
          <div style={{ background: 'linear-gradient(135deg, #0f172a, #1e3a5f)', borderRadius: 16, padding: '1.5rem 2rem', color: '#fff', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8' }}>
                {countryName}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '1rem', marginTop: '0.3rem' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '2.5rem', fontWeight: 700 }}>
                  {newMGI.toFixed(1)}
                </span>
                <span style={{ color: '#64748b', fontSize: '1rem' }}>/ 100</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <ScoreBox label="Original MGI" value={originalMGI.toFixed(2)} />
              <ScoreBox label="Direct ΔMGI" value={(deltaDirect >= 0 ? '+' : '') + deltaDirect.toFixed(2)} color={deltaDirect > 0 ? '#34d399' : deltaDirect < 0 ? '#f87171' : '#94a3b8'} />
              <ScoreBox label="Cascade ΔMGI" value={(deltaCascade >= 0 ? '+' : '') + deltaCascade.toFixed(2)} color={deltaCascade > 0 ? '#5eead4' : deltaCascade < 0 ? '#fca5a5' : '#94a3b8'} />
            </div>
          </div>

          {/* Strategy Presets */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            {[
              ['reset', 'Reset'],
              ['equal', 'Equal Distribution'],
              ['highestWeight', 'Highest Weight (LPI)'],
              ['highestEffect', 'Highest Cascade (CAPCI)'],
              ['weighted', 'Weighted Cascade (Eᵢ³)'],
            ].map(([key, label]) => (
              <button key={key} onClick={() => applyStrategy(key)} style={{
                padding: '0.45rem 0.9rem', borderRadius: 6, border: '1px solid #e2e8f0',
                background: key === 'reset' ? '#fef2f2' : '#f8fafc', cursor: 'pointer',
                fontSize: '0.82rem', fontWeight: 600, color: key === 'reset' ? '#dc2626' : '#475569',
              }}>
                {label}
              </button>
            ))}
          </div>

          {/* Sliders */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: '0.6rem', marginBottom: '2rem' }}>
            {COMPONENTS.map((comp, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', background: deltas[i] !== 0 ? '#f0fdfa' : '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <span style={{ width: 110, fontSize: '0.82rem', fontWeight: 600, color: tierColor(comp.tier) }}>
                  {comp.label}
                </span>
                <input type="range" min={0} max={100} step={0.1} value={scores[i]}
                  onChange={e => setSlider(i, parseFloat(e.target.value))}
                  style={{ flex: 1, accentColor: tierColor(comp.tier) }} />
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8rem', width: 45, textAlign: 'right', color: '#0f172a' }}>
                  {scores[i].toFixed(1)}
                </span>
                {deltas[i] !== 0 && (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.72rem', width: 48, textAlign: 'right', color: deltas[i] > 0 ? '#059669' : '#dc2626' }}>
                    {deltas[i] > 0 ? '+' : ''}{deltas[i].toFixed(1)}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Opportunity Scores */}
          <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '1.2rem', color: '#0f172a', marginBottom: '0.75rem' }}>
            Opportunity Scores — Where to invest
          </h3>
          <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '1rem' }}>
            Higher opportunity = more potential MGI improvement from investment in that component.
            Score = Total Effect × (100 − current score).
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '2rem' }}>
            {opportunity
              .map((opp, i) => ({ opp, i, label: COMPONENTS[i].label, effect: TOTAL_EFFECTS[i], headroom: 100 - originalScores[i] }))
              .sort((a, b) => b.opp - a.opp)
              .map(({ opp, i, label, effect, headroom }) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ width: 120, fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>{label}</span>
                  <div style={{ flex: 1, height: 18, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                    <div style={{ width: `${Math.min(100, opp / 50 * 100)}%`, height: '100%', background: tierColor(COMPONENTS[i].tier), borderRadius: 4, opacity: 0.7 }} />
                  </div>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.78rem', width: 45, textAlign: 'right', color: '#0f172a' }}>
                    {opp.toFixed(1)}
                  </span>
                </div>
              ))}
          </div>

          {/* Cascade explanation */}
          <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 8, padding: '1rem 1.2rem', fontSize: '0.88rem', color: '#0f766e', lineHeight: 1.7 }}>
            <strong>How to read this:</strong> The <em>Direct ΔMGI</em> is the guaranteed mechanical change from
            the MGI formula. The <em>Cascade ΔMGI</em> is the predicted long-term effect including
            interdependencies between components — for example, improving Internet access tends to correlate
            with improvements in Education, Bank access, and CAPCI over time. The cascade effect does not
            happen instantly; it represents a projected trajectory over several years.
          </div>
        </>
      )}

      {!selectedCode && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#94a3b8' }}>
          <p>Select a country above to begin the simulation.</p>
          <p style={{ fontSize: '0.9rem', maxWidth: 500, margin: '0.5rem auto' }}>
            Model policy interventions by adjusting governance components and observing
            the projected impact on the Macro-Governance Index.
          </p>
        </div>
      )}
    </div>
  );
}

function ScoreBox({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', marginBottom: '0.15rem' }}>{label}</div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.3rem', fontWeight: 700, color: color || '#e2e8f0' }}>{value}</div>
    </div>
  );
}

const labelSty = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' };
const inputSty = { width: '100%', padding: '0.7rem 1rem', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: '0.95rem', color: '#1e293b', background: '#fff', boxSizing: 'border-box' };
